import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Select } from "../../../../shared";
import DateRangeField from "../../controls/DateRangeField";
import { formatWholeNumberInput } from "../../../../CommonComponents/FormattedInput/FormattedInput";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import type { ScheduleDurationUnit, ScheduleMode } from "./toWizardCampaignIn";
import {
  DEFAULT_SCHEDULE_MODE,
  clampScheduleDuration,
  isPastOrPresentIso,
  parseScheduleDuration,
  scheduleDurationMode,
} from "./toWizardCampaignIn";
import styles from "../../stylesDynamicBonuses.module.css";

export type { ScheduleMode } from "./toWizardCampaignIn";

type UnitOption = ScheduleDurationUnit | "never" | "custom";

const DEFAULT_AMOUNT = 4;

const unitOptions = (): Array<{ value: UnitOption; label: string }> => [
  { value: "hour", label: AppStrings.PAM_DYNAMIC_BONUS_DURATION_HOURS },
  { value: "day", label: AppStrings.PAM_DYNAMIC_BONUS_DURATION_DAYS },
  {
    value: "never",
    label: AppStrings.PAM_DYNAMIC_BONUS_DURATION_NEVER_EXPIRES,
  },
  { value: "custom", label: AppStrings.PAM_DYNAMIC_BONUS_DURATION_CUSTOM },
];

const partsFromMode = (
  mode: ScheduleMode,
): { amount: number; unit: UnitOption } => {
  if (mode === "never") return { amount: DEFAULT_AMOUNT, unit: "never" };
  if (mode === "custom") return { amount: DEFAULT_AMOUNT, unit: "custom" };
  const parsed = parseScheduleDuration(mode);
  if (parsed) return parsed;
  return { amount: DEFAULT_AMOUNT, unit: "hour" };
};

type Props = {
  /** The selected duration mode (form field value). */
  value?: ScheduleMode;
  onChange?: (mode: ScheduleMode) => void;
  /** ISO pair for the custom date range — only active in "custom" mode. */
  dateRangeValue?: [string | null, string | null];
  onDateRangeChange?: (range: [string | null, string | null]) => void;
  /** Locked campaigns keep schedule controls inert. */
  disabled?: boolean;
};

/**
 * Run-for control: typed duration amount + Hours/Days (or Never / Custom).
 * Defaults to 4 hours. Positive integers compile to `Nh` / `Nd`, capped at
 * 365 days / 8760 hours so submit cannot produce an invalid Date.
 */
const ScheduleField: React.FC<Props> = ({
  value = DEFAULT_SCHEDULE_MODE,
  onChange,
  dateRangeValue,
  onDateRangeChange,
  disabled = false,
}) => {
  const lockedStart = useMemo(() => {
    const start = dateRangeValue?.[0] ?? null;
    return start && isPastOrPresentIso(start) ? start : null;
  }, [dateRangeValue]);

  const { amount, unit } = partsFromMode(value);
  const durationLocked = Boolean(lockedStart);
  const showAmount = unit === "hour" || unit === "day";
  const [amountText, setAmountText] = useState(String(amount));

  useEffect(() => {
    const parsed = parseScheduleDuration(value);
    if (parsed) setAmountText(String(parsed.amount));
  }, [value]);

  const clearOrPreserveRange = useCallback(
    (nextMode: ScheduleMode) => {
      if (nextMode === "custom") return;
      onDateRangeChange?.(lockedStart ? [lockedStart, null] : [null, null]);
    },
    [lockedStart, onDateRangeChange],
  );

  const commitMode = useCallback(
    (nextMode: ScheduleMode) => {
      if (disabled) return;
      if (durationLocked && parseScheduleDuration(nextMode)) return;
      onChange?.(nextMode);
      clearOrPreserveRange(nextMode);
    },
    [clearOrPreserveRange, disabled, durationLocked, onChange],
  );

  const handleAmountChange = useCallback(
    (raw: string) => {
      if (disabled || !showAmount) return;
      const cleaned = formatWholeNumberInput(raw);
      const parsed = parseInt(cleaned, 10);
      if (!cleaned || !Number.isFinite(parsed) || parsed <= 0) {
        setAmountText(cleaned);
        return;
      }
      if (unit !== "hour" && unit !== "day") return;
      const clamped = clampScheduleDuration(parsed, unit);
      setAmountText(String(clamped));
      commitMode(scheduleDurationMode(clamped, unit));
    },
    [commitMode, disabled, showAmount, unit],
  );

  const handleUnitChange = useCallback(
    (nextUnit: UnitOption) => {
      if (disabled) return;
      if (nextUnit === "never") {
        commitMode("never");
        return;
      }
      if (nextUnit === "custom") {
        onChange?.("custom");
        return;
      }
      if (durationLocked) return;
      if (nextUnit !== "hour" && nextUnit !== "day") return;
      const typed = parseInt(amountText, 10);
      const nextAmount =
        Number.isFinite(typed) && typed > 0 ? typed : DEFAULT_AMOUNT;
      const clamped = clampScheduleDuration(nextAmount, nextUnit);
      setAmountText(String(clamped));
      commitMode(scheduleDurationMode(clamped, nextUnit));
    },
    [amountText, commitMode, disabled, durationLocked, onChange],
  );

  const handleDateRangeChange = useCallback(
    (range: [string | null, string | null]) => {
      if (disabled) return;
      if (lockedStart) {
        onDateRangeChange?.([lockedStart, range[1]]);
        return;
      }
      onDateRangeChange?.(range);
    },
    [disabled, lockedStart, onDateRangeChange],
  );

  const unitSelectOptions = useMemo(
    () =>
      unitOptions().map((option) => ({
        ...option,
        disabled:
          durationLocked &&
          (option.value === "hour" || option.value === "day"),
      })),
    [durationLocked],
  );

  return (
    <div
      className={disabled ? styles.readOnlyFields : undefined}
      ref={(element) => {
        if (disabled) {
          element?.setAttribute("inert", "");
        } else {
          element?.removeAttribute("inert");
        }
      }}
      aria-disabled={disabled || undefined}
    >
      <div
        className={styles.runForControl}
        role="group"
        aria-label={AppStrings.PAM_DYNAMIC_BONUS_DURATION_ARIA}
      >
        {showAmount && (
          <Input
            className={styles.runForAmount}
            value={amountText}
            disabled={disabled || durationLocked}
            onChange={(event) => handleAmountChange(event.target.value)}
            aria-label={AppStrings.PAM_DYNAMIC_BONUS_DURATION_AMOUNT_ARIA}
            inputMode="numeric"
          />
        )}
        <Select
          className={`${styles.runForUnit}${
            showAmount ? "" : ` ${styles.runForUnitFull}`
          }`}
          value={unit}
          options={unitSelectOptions}
          disabled={disabled}
          onChange={(next) => handleUnitChange(next as UnitOption)}
          aria-label={AppStrings.PAM_DYNAMIC_BONUS_DURATION_UNIT_ARIA}
          popupMatchSelectWidth={false}
        />
      </div>

      {lockedStart && !disabled && (
        <p className={styles.stepHint} style={{ marginTop: 8 }}>
          {AppStrings.PAM_DYNAMIC_BONUS_SCHEDULE_START_LOCKED}
        </p>
      )}

      {value === "custom" && (
        <div className={styles.scheduleDateRange}>
          <DateRangeField
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            showPresets={false}
            lockStart={Boolean(lockedStart)}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default ScheduleField;
